import { useQuery } from "@tanstack/solid-query";
import type { OverviewResponse } from "contracts/api/dashboard/responses";
import type { WebsiteSettingsResponse } from "contracts/api/website/settings";
import type { ScrapeCountryCode } from "contracts/enums/scrape-country";
import { createSignal, Show } from "solid-js";
import { linesEntered } from "../../boundaries/user-list-input.parser";
import { isAdmin } from "../../store/admin";
import { api } from "../../store/api";
import { socialEnabled } from "../../store/demo";
import { queryClient } from "../../store/query";
import {
  NumberField,
  SelectField,
  TextArea,
  TextField,
} from "../admin/editorFields";
import { ModuleShell } from "../scan/ModuleShell";
import {
  COUNTRY_FIELD_LABEL,
  COUNTRY_OPTIONS,
} from "../website/countryOptions";
import { createWebsiteStore } from "../website/store";
import { DangerZone } from "./DangerZone";
import { SocialSettings } from "./SocialSettings";

export function settingsQueryKey(websiteId: string): [string, string] {
  return ["website-settings", websiteId];
}

export function WebsiteSettings(props: { websiteId: string }) {
  const context = createWebsiteStore(props.websiteId);
  const settings = useQuery(() => ({
    queryKey: settingsQueryKey(props.websiteId),
    queryFn: () => api.websiteSettings(props.websiteId),
  }));

  return (
    <ModuleShell
      context={context}
      activeModule="settings"
      workspaceClass="signal-overview-workspace"
    >
      {/* keyed: saving writes the PATCH response into this query's cache,
          which remounts the form with the server-normalized values. */}
      <Show
        when={settings.data}
        keyed
        fallback={<div class="loading signal-loading">Loading settings...</div>}
      >
        {(value) => <SettingsContent initial={value} />}
      </Show>
      <Show when={socialEnabled() && context.data()?.site.clientId}>
        {(clientId) => <SocialSettings clientId={clientId()} />}
      </Show>
    </ModuleShell>
  );
}

function SettingsContent(props: { initial: WebsiteSettingsResponse }) {
  const websiteId = props.initial.websiteId;

  const [displayName, setDisplayName] = createSignal(
    props.initial.displayName ?? "",
  );
  const [maxPages, setMaxPages] = createSignal(props.initial.config.maxPages);
  const [maxDepth, setMaxDepth] = createSignal(props.initial.config.maxDepth);
  const [countryCode, setCountryCode] = createSignal<ScrapeCountryCode>(
    props.initial.config.countryCode,
  );
  const [seedUrls, setSeedUrls] = createSignal(
    (props.initial.config.seedUrls ?? []).join("\n"),
  );
  const [saving, setSaving] = createSignal(false);
  const [saveError, setSaveError] = createSignal<string | null>(null);
  const parsedSeedUrls = () => linesEntered(seedUrls());

  const save = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      const seeds = parsedSeedUrls();
      const saved = await api.updateWebsiteSettings(websiteId, {
        displayName: displayName().trim() || null,
        config: {
          maxPages: maxPages(),
          maxDepth: maxDepth(),
          countryCode: countryCode(),
          ...(seeds.length > 0 ? { seedUrls: seeds } : {}),
        },
      });
      queryClient.setQueryData(settingsQueryKey(websiteId), saved);
      queryClient.setQueryData<OverviewResponse>(
        ["overview", websiteId],
        (current) =>
          current && {
            ...current,
            site: { ...current.site, displayName: saved.displayName },
          },
      );
    } catch (error) {
      setSaveError(error instanceof Error ? error.message : String(error));
    } finally {
      setSaving(false);
    }
  };

  const websiteName = () =>
    props.initial.displayName ?? props.initial.clientName;

  return (
    <div class="signal-overview-page">
      <header class="signal-overview-head">
        <div>
          <h1>Website settings</h1>
          <p>
            Changes apply to future scans. Existing results retain the settings
            they were run with.
          </p>
        </div>
      </header>

      <section class="editor">
        <h2>Organisation and website</h2>
        <p>
          The organisation and website URL identify this website and cannot be
          changed. To analyse a different website, add a new website.
        </p>
        <div class="editor-field">
          <span class="editor-field-label">Organisation</span>
          <span>{props.initial.clientName}</span>
        </div>
        <div class="editor-field">
          <span class="editor-field-label">Website URL</span>
          <span class="mono">{props.initial.originUrl}</span>
        </div>
      </section>

      <section class="editor">
        <h2>Website name</h2>
        <TextField
          label="Website name (optional)"
          value={displayName()}
          onInput={setDisplayName}
        />
      </section>

      <section class="editor">
        <h2>Scan settings</h2>
        <p>
          Saved settings are versioned; existing results retain the settings
          they were run with.
        </p>
        <NumberField
          label="Page limit (1-1000)"
          value={maxPages()}
          onInput={setMaxPages}
        />
        <NumberField
          label="Link depth (0 = seed URLs only)"
          value={maxDepth()}
          onInput={setMaxDepth}
        />
        <SelectField
          label={COUNTRY_FIELD_LABEL}
          value={countryCode()}
          options={COUNTRY_OPTIONS}
          onInput={setCountryCode}
        />
        <TextArea
          label="Seed URLs, one per line (empty = start from the website URL)"
          value={seedUrls()}
          onInput={setSeedUrls}
        />
        <button
          class="signal-scan-btn"
          type="button"
          onClick={save}
          disabled={saving() || !isAdmin()}
          title={isAdmin() ? "Save settings" : "Sign in to edit"}
        >
          {saving() ? "Saving..." : "Save settings"}
        </button>
        <Show when={saveError()}>
          <span class="signal-scan-error" role="alert">
            {saveError()}
          </span>
        </Show>
      </section>

      <DangerZone websiteId={websiteId} websiteName={websiteName()} />
    </div>
  );
}
